import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowLeft, Check, Send, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { num: 1, title: "Your Hunt", sub: "What are you after?" },
  { num: 2, title: "Your Experience", sub: "Tell us about yourself." },
  { num: 3, title: "Budget & Contact", sub: "Let's get you booked." },
];

const REGIONS = [
  { value: "alaska", label: "Alaska" },
  { value: "lower_48", label: "Montana & Lower 48" },
  { value: "canada", label: "Canada" },
  { value: "central_america", label: "Central America" },
  { value: "south_america", label: "South America" },
  { value: "africa", label: "Africa" },
  { value: "europe", label: "Europe" },
  { value: "central_asia", label: "Central Asia" },
  { value: "russia_kamchatka", label: "Russia / Kamchatka" },
  { value: "oceania", label: "Oceania" },
  { value: "south_asia", label: "South Asia" },
  { value: "open", label: "Open to Suggestions" },
];

const SPECIES_CATEGORIES = [
  { value: "Big Game", label: "Big Game" },
  { value: "Dangerous Game", label: "Dangerous Game" },
  { value: "Mountain Game", label: "Mountain Game" },
  { value: "Plains Game", label: "Plains Game" },
  { value: "Waterfowl", label: "Waterfowl" },
  { value: "Wingshooting", label: "Wingshooting" },
  { value: "Fishing", label: "Fishing" },
  { value: "Predators", label: "Predators" },
];

const EXPERIENCE_LEVELS = [
  "First hunt / new to guided hunting",
  "A few guided hunts under my belt",
  "Experienced — 10+ guided trips",
  "Very experienced — international hunts included",
];

const FITNESS_LEVELS = [
  "Lodge-based, easy terrain",
  "Moderate — day hikes, some elevation",
  "Athletic — long treks, mountain terrain",
  "Extreme — high altitude, multi-day backcountry",
];

const GROUP_SIZES = ["Solo", "2 hunters", "3-4 hunters", "5+ hunters"];

const BUDGET_RANGES = [
  { value: "under_5k", label: "Under $5,000" },
  { value: "5k_15k", label: "$5,000 – $15,000" },
  { value: "15k_25k", label: "$15,000 – $25,000" },
  { value: "25k_50k", label: "$25,000 – $50,000" },
  { value: "50k_100k", label: "$50,000 – $100,000" },
  { value: "100k_plus", label: "$100,000+" },
];

const HOW_HEARD = ["Google", "Referral", "Social Media", "Hunting Forum", "Trade Show", "Other"];

const INITIAL = {
  adventure_type: "hunting",
  regions: [],
  species_categories: [],
  specific_species: "",
  experience_level: "",
  fitness_level: "",
  comfort_preference: "",
  hunting_methods: [],
  fishing_methods: [],
  group_size: "",
  budget_range: "",
  name: "",
  email: "",
  phone: "",
  preferred_dates: "",
  special_requests: "",
  how_heard: "",
};

function SelectChip({ selected, onClick, children, testId }) {
  return (
    <button
      type="button" onClick={onClick}
      className={`px-4 py-2.5 text-sm font-label font-semibold tracking-wider uppercase transition-all border
        ${selected ? "bg-brass text-bone border-brass" : "bg-bone border-carbon/10 text-carbon hover:border-brass/40"}`}
      data-testid={testId}
    >{children}</button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-label text-smoke mb-2 text-[11px]">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, type = "text", placeholder, testId }) {
  return (
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full h-12 px-4 bg-bone border border-carbon/10 font-body text-sm text-carbon placeholder:text-smoke/50 focus:outline-none focus:border-brass transition-colors"
      data-testid={testId} />
  );
}

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full h-12 px-4 bg-bone border border-carbon/10 font-body text-sm text-carbon focus:outline-none focus:border-brass transition-colors appearance-none"
      data-testid={testId}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function FindYourHunt() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setForm((p) => ({
    ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v],
  }));

  const canNext = () => {
    if (step === 1) return form.regions.length > 0 && form.species_categories.length > 0;
    if (step === 2) return form.experience_level && form.fitness_level && form.group_size;
    if (step === 3) return form.budget_range && form.name && form.email && form.phone;
    return false;
  };

  const handleSubmit = async () => {
    if (!canNext()) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || (Array.isArray(payload[k]) && payload[k].length === 0)) delete payload[k];
      });
      if (!payload.adventure_type) payload.adventure_type = "hunting";
      if (!payload.regions) payload.regions = [];
      if (!payload.species_categories) payload.species_categories = [];
      if (!payload.comfort_preference) payload.comfort_preference = "N/A";

      await axios.post(`${API}/api/hunt-finder`, payload);
      setSubmitted(true);
      toast.success("Application received. We'll be in touch.");
    } catch {
      toast.error("Something went wrong. Try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <Helmet><title>Application Received | Dispatch Hunts</title></Helmet>
        <section className="min-h-screen bg-bone pt-32 pb-20">
          <div className="container-dh max-w-xl text-center">
            <div className="w-14 h-14 bg-brass flex items-center justify-center mx-auto mb-6">
              <Check className="w-7 h-7 text-bone" />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-carbon mb-4" data-testid="confirmation-headline">
              We've got your application.
            </h1>
            <p className="font-body text-smoke leading-relaxed mb-8">
              Our team will review your details and reach out within 24 hours. If your hunt is time-sensitive, call directly.
            </p>
            <a href="tel:4065805366" className="btn-dark" data-testid="confirmation-call-btn">
              <Phone className="w-4 h-4" /> Call Us: 406-580-5366
            </a>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Apply for a Consultation | Dispatch Hunts</title></Helmet>

      {/* Header */}
      <section className="bg-carbon grain pt-28 pb-12">
        <div className="container-dh max-w-2xl text-center">
          <p className="text-label text-brass mb-4">Consultation Application</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-bone mb-3" data-testid="apply-headline">
            Tell us what you're after.
          </h1>
          <p className="font-body text-bone/50 text-sm">
            Three quick steps. We'll follow up personally.
          </p>
          {/* Progress */}
          <div className="flex items-center gap-1 max-w-xs mx-auto mt-8" data-testid="progress-bar">
            {STEPS.map((s, i) => (
              <div key={s.num} className="flex items-center flex-1">
                <div className={`w-8 h-8 flex items-center justify-center font-mono text-xs font-bold transition-colors
                  ${i + 1 < step ? "bg-brass text-bone" : i + 1 === step ? "bg-bone text-carbon" : "bg-bone/10 text-bone/30"}`}>
                  {i + 1 < step ? <Check className="w-4 h-4" /> : s.num}
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-[2px] mx-1 ${i + 1 < step ? "bg-brass" : "bg-bone/10"}`} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Body */}
      <section className="bg-chalk min-h-[50vh]">
        <div className="container-dh max-w-2xl py-12">
          <p className="text-label text-brass mb-2 text-[10px]" data-testid="step-label">Step {step} of 3</p>
          <h2 className="font-display text-2xl font-bold text-carbon mb-8" data-testid="step-title">{STEPS[step - 1].sub}</h2>

          {/* Step 1 */}
          {step === 1 && (
            <div className="space-y-8" data-testid="step-1-content">
              <Field label="Where do you want to hunt?">
                <div className="flex flex-wrap gap-2">
                  {REGIONS.map((r) => (
                    <SelectChip key={r.value} selected={form.regions.includes(r.value)} onClick={() => toggle("regions", r.value)} testId={`region-${r.value}`}>
                      {r.label}
                    </SelectChip>
                  ))}
                </div>
              </Field>
              <Field label="What kind of hunting?">
                <div className="flex flex-wrap gap-2">
                  {SPECIES_CATEGORIES.map((c) => (
                    <SelectChip key={c.value} selected={form.species_categories.includes(c.value)} onClick={() => toggle("species_categories", c.value)} testId={`cat-${c.value.toLowerCase().replace(/\s/g, "-")}`}>
                      {c.label}
                    </SelectChip>
                  ))}
                </div>
              </Field>
              <Field label="Specific species in mind? (optional)">
                <Input value={form.specific_species} onChange={set("specific_species")} placeholder="e.g., Brown Bear, Marco Polo, Elk" testId="specific-species-input" />
              </Field>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2-content">
              <Field label="Hunting experience">
                <Select value={form.experience_level} onChange={set("experience_level")} options={EXPERIENCE_LEVELS} placeholder="Select" testId="experience-select" />
              </Field>
              <Field label="Physical fitness / terrain comfort">
                <Select value={form.fitness_level} onChange={set("fitness_level")} options={FITNESS_LEVELS} placeholder="Select" testId="fitness-select" />
              </Field>
              <Field label="Group size">
                <Select value={form.group_size} onChange={set("group_size")} options={GROUP_SIZES} placeholder="Select" testId="group-select" />
              </Field>
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div className="space-y-8" data-testid="step-3-content">
              <Field label="Budget range (per person)">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {BUDGET_RANGES.map((b) => (
                    <button key={b.value} type="button" onClick={() => set("budget_range")(b.value)}
                      className={`p-3 text-center font-mono text-sm border transition-all
                        ${form.budget_range === b.value ? "border-brass bg-brass/5 text-carbon font-bold" : "border-carbon/10 bg-bone text-smoke hover:border-brass/30"}`}
                      data-testid={`budget-${b.value}`}>{b.label}</button>
                  ))}
                </div>
              </Field>
              <div className="border-t border-carbon/10 pt-8 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Full Name"><Input value={form.name} onChange={set("name")} placeholder="John Smith" testId="name-input" /></Field>
                  <Field label="Phone"><Input value={form.phone} onChange={set("phone")} type="tel" placeholder="(406) 555-0123" testId="phone-input" /></Field>
                </div>
                <Field label="Email"><Input value={form.email} onChange={set("email")} type="email" placeholder="john@example.com" testId="email-input" /></Field>
                <Field label="Preferred travel dates (optional)"><Input value={form.preferred_dates} onChange={set("preferred_dates")} placeholder="e.g., September 2026" testId="dates-input" /></Field>
                <Field label="How did you hear about us?">
                  <Select value={form.how_heard} onChange={set("how_heard")} options={HOW_HEARD} placeholder="Select (optional)" testId="heard-select" />
                </Field>
                <Field label="Anything else we should know? (optional)">
                  <textarea value={form.special_requests} onChange={(e) => set("special_requests")(e.target.value)}
                    placeholder="Physical limitations, specific outfitter requests, companions, etc."
                    rows={3} className="w-full px-4 py-3 bg-bone border border-carbon/10 font-body text-sm text-carbon placeholder:text-smoke/50 focus:outline-none focus:border-brass transition-colors resize-none"
                    data-testid="notes-textarea" />
                </Field>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-12 pt-8 border-t border-carbon/10" data-testid="form-navigation">
            {step > 1 ? (
              <button type="button" onClick={() => setStep((s) => s - 1)} className="btn-outline-dark !py-3" data-testid="back-button">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : <div />}
            {step < 3 ? (
              <button type="button" onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()}
                className={`btn-brass !py-3 ${!canNext() ? "opacity-30 cursor-not-allowed" : ""}`} data-testid="next-button">
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={!canNext() || submitting}
                className={`btn-brass !py-3 ${!canNext() || submitting ? "opacity-30 cursor-not-allowed" : ""}`} data-testid="submit-button">
                {submitting ? "Submitting..." : <><Send className="w-4 h-4" /> Submit Application</>}
              </button>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
